/**
 * Shared helpers for in-Runner script editing.
 * Used by NewClientIntakeForm and PropertyIntakeForm to expose per-field
 * pencil (✎) edits, [↑]/[↓] reorder buttons, and header title/description
 * inline edits — all PATCHing the underlying workflow template.
 */
import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import { Pencil, ArrowUp, ArrowDown, Check, X, Loader2 } from 'lucide-react';

const api = axios.create({ baseURL: `${process.env.REACT_APP_BACKEND_URL}/api` });
api.interceptors.request.use(cfg => {
  const t = localStorage.getItem('access_token');
  if (t) cfg.headers.Authorization = `Bearer ${t}`;
  return cfg;
});

/** Serialise an ordered [{key, prompt}] list back to the "key: text" format. */
export function serialisePrompts(items) {
  return items.map(i => `${i.key}: ${i.prompt}`).join('\n');
}

/** PATCH a single param on the given action. Bubbles up onTemplateChanged() when done. */
export async function patchActionParams(editCtx, patchParams) {
  if (!editCtx) return;
  await api.patch(
    `/status-workflows/workflow/${editCtx.statusKey}/tasks/${editCtx.taskId}/actions/${editCtx.actionId}`,
    { params: patchParams },
  );
  if (editCtx.onTemplateChanged) await editCtx.onTemplateChanged();
}

/**
 * Shared onKeyDown handler for data-entry forms in the runner.
 * Enter on a single-line input/select → prevent the default form-submit and
 * advance focus to the next tabbable input inside the same wrapper. Textareas
 * keep native newline behaviour so multi-line notes work as expected.
 * Attach to a `<form>` or wrapping `<div>` via `onKeyDown={advanceOnEnter}`.
 * iter303aj-fix3 — matches the tab-order flow to the visible question order.
 */
export function advanceOnEnter(e) {
  // iter303ak-fix56 · Also honour Tab (with or without Shift): Trevor
  // asked for Tab to advance like Enter. Shift+Tab moves backwards.
  const isEnter = e.key === 'Enter';
  const isTab = e.key === 'Tab';
  if (!isEnter && !isTab) return;
  const t = e.target;
  const tag = (t.tagName || '').toUpperCase();
  // Keep native behaviour for textareas + explicit submit/reset buttons.
  if (tag === 'TEXTAREA') return;
  if (tag === 'BUTTON' && ((t.type || '').toLowerCase() === 'submit' || (t.type || '').toLowerCase() === 'reset')) return;
  // Only intercept the field types operators actually type into.
  if (tag !== 'INPUT' && tag !== 'SELECT') return;
  const inputType = (t.type || '').toLowerCase();
  // Native checkbox/radio: Enter should not toggle nor submit — swallow it.
  if (inputType === 'checkbox' || inputType === 'radio') {
    if (isEnter) e.preventDefault();
    return;
  }
  e.preventDefault();
  // Collect all real focusable inputs inside the wrapper, in DOM order.
  const scope = e.currentTarget;
  const focusables = Array.from(scope.querySelectorAll('input, select, textarea')).filter(el => {
    if (el.disabled || el.readOnly) return false;
    if (el.tabIndex < 0) return false;
    if (el.type === 'hidden') return false;
    // offsetParent === null means element is not visible (display:none / detached).
    return el.offsetParent !== null;
  });
  const idx = focusables.indexOf(t);
  if (idx === -1) return;
  // Shift+Tab → move backwards; Enter / Tab → move forwards.
  const step = (isTab && e.shiftKey) ? -1 : 1;
  const next = focusables[idx + step];
  if (next) next.focus();
}

/** Small pencil-edit widget for a one-line label; renders text w/ hover pencil
 *  or an inline textarea when active. Persists via patchActionParams. */
export function InlineParamEdit({ editCtx, paramKey, value, placeholder,
                                   className = 'text-slate-700', dataTestidPrefix = 'inline',
                                   multiline = false, alwaysShowPencil = false }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value || '');
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
  useEffect(() => { setDraft(value || ''); }, [value]);

  const enabled = !!editCtx;
  if (!enabled) return <span className={className}>{value || placeholder}</span>;

  const save = async () => {
    setSaving(true); setErr('');
    try {
      await patchActionParams(editCtx, { [paramKey]: draft.trim() });
      setEditing(false);
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message || 'Save failed');
    } finally { setSaving(false); }
  };

  if (editing) {
    return (
      <div className="inline-flex flex-col gap-1 w-full">
        {multiline ? (
          <textarea value={draft} onChange={e => setDraft(e.target.value)}
            rows={2} autoFocus
            data-testid={`${dataTestidPrefix}-input`}
            className="text-sm px-2 py-1 border border-sky-300 rounded focus:outline-none focus:ring-2 focus:ring-sky-300 w-full" />
        ) : (
          <input value={draft} onChange={e => setDraft(e.target.value)}
            autoFocus
            data-testid={`${dataTestidPrefix}-input`}
            className="text-sm px-2 py-1 border border-sky-300 rounded focus:outline-none focus:ring-2 focus:ring-sky-300 w-full" />
        )}
        <div className="flex items-center gap-2">
          <button type="button" onClick={save} disabled={saving}
            data-testid={`${dataTestidPrefix}-save`}
            className="px-2 py-0.5 bg-sky-600 text-white text-[10px] rounded hover:bg-sky-700 disabled:opacity-40 flex items-center gap-1">
            {saving ? <Loader2 size={10} className="animate-spin" /> : <Check size={10} />} Save
          </button>
          <button type="button" onClick={() => { setEditing(false); setDraft(value || ''); setErr(''); }}
            data-testid={`${dataTestidPrefix}-cancel`}
            className="px-2 py-0.5 text-slate-500 text-[10px] hover:text-slate-700 flex items-center gap-1">
            <X size={10} /> Cancel
          </button>
          {err && <span className="text-[10px] text-rose-600">{err}</span>}
        </div>
      </div>
    );
  }

  return (
    <span className={`group inline-flex items-start gap-1 ${className}`}>
      <span>{value || <span className="text-slate-300 italic">{placeholder}</span>}</span>
      <button type="button" onClick={() => setEditing(true)}
        data-testid={`${dataTestidPrefix}-btn`}
        title="Edit"
        tabIndex={-1}
        className={`shrink-0 text-slate-300 hover:text-sky-500 ${alwaysShowPencil ? '' : 'opacity-0 group-hover:opacity-100'} transition`}>
        <Pencil size={12} />
      </button>
    </span>
  );
}

/** Wraps a field row with hover-visible ↑ ↓ ✎ controls. Order changes PATCH
 *  the parent action's `prompts:` param. Text edit PATCHes the same param. */
export function FieldRowControls({ editCtx, items, idx, currentPrompt, onPromptChange, canReorder = true }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(currentPrompt || '');
  const [saving, setSaving] = useState(false);
  useEffect(() => { setDraft(currentPrompt || ''); }, [currentPrompt]);

  if (!editCtx) return null;

  const commitReorder = async (dir) => {
    const j = idx + dir;
    if (j < 0 || j >= items.length) return;
    const arr = items.slice();
    const tmp = arr[idx]; arr[idx] = arr[j]; arr[j] = tmp;
    await patchActionParams(editCtx, { prompts: serialisePrompts(arr) });
  };

  const commitText = async () => {
    setSaving(true);
    try {
      const arr = items.slice();
      arr[idx] = { ...arr[idx], prompt: draft.trim() };
      await patchActionParams(editCtx, { prompts: serialisePrompts(arr) });
      if (onPromptChange) onPromptChange(draft.trim());
      setEditing(false);
    } finally { setSaving(false); }
  };

  return (
    <div className="flex items-start gap-1">
      {canReorder && (
        <>
          <button type="button" onClick={() => commitReorder(-1)} disabled={idx === 0}
            data-testid={`fieldrow-up-${idx}`}
            title="Move up"
            tabIndex={-1}
            className="p-1 text-slate-300 hover:text-slate-700 disabled:opacity-20 opacity-0 group-hover:opacity-100 transition">
            <ArrowUp size={12} />
          </button>
          <button type="button" onClick={() => commitReorder(+1)} disabled={idx === items.length - 1}
            data-testid={`fieldrow-down-${idx}`}
            title="Move down"
            tabIndex={-1}
            className="p-1 text-slate-300 hover:text-slate-700 disabled:opacity-20 opacity-0 group-hover:opacity-100 transition">
            <ArrowDown size={12} />
          </button>
        </>
      )}
      {editing ? (
        <div className="flex items-center gap-1">
          <textarea value={draft} onChange={e => setDraft(e.target.value)}
            rows={2} autoFocus
            data-testid={`fieldrow-input-${idx}`}
            className="text-xs px-2 py-1 border border-sky-300 rounded w-96 focus:outline-none focus:ring-2 focus:ring-sky-300" />
          <button type="button" onClick={commitText} disabled={saving}
            data-testid={`fieldrow-save-${idx}`}
            title="Save script"
            className="p-1 rounded bg-sky-600 text-white hover:bg-sky-700 disabled:opacity-40">
            {saving ? <Loader2 size={12} className="animate-spin" /> : <Check size={12} />}
          </button>
          <button type="button" onClick={() => { setEditing(false); setDraft(currentPrompt || ''); }}
            data-testid={`fieldrow-cancel-${idx}`}
            className="p-1 text-slate-400 hover:text-slate-600">
            <X size={12} />
          </button>
        </div>
      ) : (
        <button type="button" onClick={() => setEditing(true)}
          data-testid={`fieldrow-edit-${idx}`}
          title="Edit script"
          tabIndex={-1}
          className="p-1 text-slate-300 hover:text-sky-500 opacity-0 group-hover:opacity-100 transition">
          <Pencil size={12} />
        </button>
      )}
    </div>
  );
}
